import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Upload, FolderOpen } from 'lucide-react';

export const ImportDialog = ({ open, onOpenChange, macroProjects, onImport, onImportHolidays }) => {
  const [importMode, setImportMode] = useState('gantt'); // 'gantt' | 'holidays'
  const [selectedMacroProject, setSelectedMacroProject] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);

  const handleFileChange = function(e) {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleSubmit = function(e) {
    e.preventDefault();
    if (!selectedFile) return;

    if (importMode === 'gantt') {
      onImport(selectedFile, selectedMacroProject || null);
    } else {
      if (onImportHolidays) {
        onImportHolidays(selectedFile);
      }
    }

    onOpenChange(false);
    setSelectedFile(null);
    setSelectedMacroProject('');
  };

  const handleClose = function() {
    onOpenChange(false);
    setSelectedFile(null);
    setSelectedMacroProject('');
  };

  const handleModeChange = function(mode) {
    setImportMode(mode);
    setSelectedFile(null);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Importa Dati
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Mode selector */}
          <div className="space-y-2">
            <Label>Tipo di importazione</Label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={function() { handleModeChange('gantt'); }}
                className={
                  'flex-1 py-2 px-3 rounded-md border text-sm font-medium transition-colors ' +
                  (importMode === 'gantt'
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background border-input hover:bg-muted')
                }
              >
                Gantt (Excel)
              </button>
              <button
                type="button"
                onClick={function() { handleModeChange('holidays'); }}
                className={
                  'flex-1 py-2 px-3 rounded-md border text-sm font-medium transition-colors ' +
                  (importMode === 'holidays'
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background border-input hover:bg-muted')
                }
              >
                Piano Ferie (CSV)
              </button>
            </div>
          </div>

          {/* Gantt-specific: macro project selector */}
          {importMode === 'gantt' && (
            <div className="space-y-2">
              <Label>Macro Progetto di destinazione</Label>
              <select
                value={selectedMacroProject}
                onChange={function(e) { setSelectedMacroProject(e.target.value); }}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                data-testid="import-macro-project-select"
              >
                <option value="">-- Nessun Macro Progetto --</option>
                {macroProjects.map(function(mp) {
                  return (
                    <option key={mp.id} value={mp.id}>
                      {mp.name}
                    </option>
                  );
                })}
              </select>
              <p className="text-xs text-muted-foreground">
                I progetti importati saranno associati a questo macro progetto
              </p>
            </div>
          )}

          {/* File picker */}
          <div className="space-y-2">
            <Label>{importMode === 'gantt' ? 'File Excel' : 'File CSV Piano Ferie'}</Label>
            <div
              className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary/50 transition-colors cursor-pointer"
              onClick={function() { document.getElementById('import-file-input').click(); }}
            >
              <input
                id="import-file-input"
                type="file"
                accept={importMode === 'gantt' ? '.xlsx,.xls' : '.csv'}
                className="hidden"
                onChange={handleFileChange}
              />
              {selectedFile ? (
                <div className="flex items-center justify-center gap-2 text-primary">
                  <FolderOpen className="w-5 h-5" />
                  <span className="font-medium">{selectedFile.name}</span>
                </div>
              ) : (
                <div className="text-muted-foreground">
                  <Upload className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">
                    {importMode === 'gantt'
                      ? 'Clicca per selezionare un file .xlsx'
                      : 'Clicca per selezionare il file CSV piano ferie'}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Info box */}
          <div className="text-xs text-muted-foreground bg-muted/50 p-3 rounded-md">
            {importMode === 'gantt' ? (
              <>
                <p className="font-medium mb-1">Formato atteso (Excel):</p>
                <p>Colonne: Progetto, Task, Data Inizio, Data Fine, Avanzamento (%), Risorse</p>
                <p>Foglio opzionale: "Risorse" (Nome, Avatar) per importare l'anagrafica risorse</p>
              </>
            ) : (
              <>
                <p className="font-medium mb-1">Formato atteso (CSV Piano Ferie Hera):</p>
                <p>Intestazioni: Mese Anno, Giorno, Persona</p>
                <p>Valore 8 in corrispondenza del giorno di ferie, vuoto altrimenti</p>
                <p className="mt-1 text-amber-600 dark:text-amber-400">Le risorse non presenti a sistema saranno ignorate</p>
              </>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Annulla
            </Button>
            <Button type="submit" disabled={!selectedFile} data-testid="confirm-import-btn">
              Importa
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ImportDialog;
